import { panelIdsEqual } from '@/common/utils/panelChildShellMembership'

export type LivePanelVisibilityEntry = {
  formId: string
  panelId: string | number
  instanceKey?: string
  runtimeId?: string
  setVisible?: (visible: boolean) => void
}

export type LivePanelVisibilityRegistry = Record<string, LivePanelVisibilityEntry[]>

function runtimeIdOf(entry: { runtimeId?: string } | null | undefined): string {
  return String(entry?.runtimeId ?? '').trim()
}

function instanceKeyOf(entry: { instanceKey?: string } | null | undefined): string {
  return String(entry?.instanceKey ?? 'default')
}

/** Upsert by runtimeId so remount with the same tiles instanceKey keeps both until each unmounts. */
export function upsertPanelVisibilityEntries(
  prev: LivePanelVisibilityEntry[] | undefined,
  entry: LivePanelVisibilityEntry,
): LivePanelVisibilityEntry[] {
  const list = Array.isArray(prev) ? [...prev] : []
  const runtimeId = runtimeIdOf(entry)
  const next = runtimeId
    ? list.filter((item) => runtimeIdOf(item) !== runtimeId)
    : list.filter((item) => instanceKeyOf(item) !== instanceKeyOf(entry))
  next.push(entry)
  return next
}

/** Remove only the matching runtimeId (fallback: instanceKey for callers without uid). */
export function removePanelVisibilityEntries(
  prev: LivePanelVisibilityEntry[] | undefined,
  match: { runtimeId?: string; instanceKey?: string },
): LivePanelVisibilityEntry[] {
  const list = Array.isArray(prev) ? prev : []
  const runtimeId = String(match.runtimeId ?? '').trim()
  if (runtimeId) {
    return list.filter((item) => runtimeIdOf(item) !== runtimeId)
  }
  const instanceKey = instanceKeyOf(match)
  return list.filter((item) => instanceKeyOf(item) !== instanceKey)
}

export function replacePanelVisibilityRegistryList(
  registry: LivePanelVisibilityRegistry,
  registryKey: string,
  next: LivePanelVisibilityEntry[],
): LivePanelVisibilityRegistry {
  if (next.length === 0) {
    const copy = { ...registry }
    delete copy[registryKey]
    return copy
  }
  return { ...registry, [registryKey]: next }
}

export function applyPanelVisibilityInRegistry(
  registry: LivePanelVisibilityRegistry,
  panelId: string,
  isVisible: boolean,
): boolean {
  let applied = false
  for (const entries of Object.values(registry)) {
    for (const entry of entries ?? []) {
      if (panelIdsEqual(entry.panelId, panelId) && entry.setVisible) {
        entry.setVisible(isVisible)
        applied = true
      }
    }
  }
  return applied
}
